/* eslint-disable no-console */
import {
  SCHWAB_ONE_PERSONAL,
  SCHWAB_IRA,
  SCHWAB_ONE_TRUST,
  SCHWAB_CHARITABLE,
  getFinancialAccountData,
  shouldShowOnlyStandardSections,
  rawFinancialAccountTypes,
} from 'utils/financialAccountTypes'

import C from 'actionTypes/onboarding/routing'

import sectionsList from './sectionsList'
import store from 'store'

export const continueToNextSection = (props) => {
  const nextSectionAction = getNextSection(props)
  return store.dispatch(nextSectionAction)
}

const getNextFinancialAccount = (currentFinancialAccountId, financialAccounts) => {
  const currentIndex = financialAccounts.map(f => f.id).indexOf(currentFinancialAccountId)
  const nextFinAccount = financialAccounts[currentIndex + 1]
  const nextFinancialAccount = (currentIndex + 1) < financialAccounts.length && nextFinAccount
  return nextFinancialAccount
}

const getPrevFinancialAccount = (currentFinancialAccountId, financialAccounts) => {
  const currentIndex = financialAccounts.map(f => f.id).indexOf(currentFinancialAccountId)
  const prevFinAccount = (currentIndex - 1) >= 0 && financialAccounts[currentIndex - 1]
  return prevFinAccount
}

const getFinancialAccount = (financialAccounts, financialAccountId) => (
  financialAccounts && financialAccounts.find(financialAccount =>
    financialAccount.id === financialAccountId)
)

const sectionInformation = (financialAccountType, section) => {
  const sections = sectionsList[`${financialAccountType}`]
  let overrides = {}
  const currentSection = sections[section]

  if (currentSection.contactType) {
    overrides = {
      ...overrides,
      contactType: currentSection.contactType,
    }
  }

  if (currentSection.beneficiarySubType) {
    overrides = {
      ...overrides,
      isSuccessorDonor: currentSection.beneficiarySubType === 'Successor Donor'
    }
  }

  return {
    to: { type: currentSection.sectionType },
    name: currentSection.name,
    standardSection: currentSection.standardSection,
    showBackWord: true,
    ...overrides,
  }
}

const buildSectionList = (navConfig, accounts, accountId) => {
  let sections = Object.keys(navConfig)
  const currentFinancialAccount = getFinancialAccount(accounts, accountId)
  if (shouldShowOnlyStandardSections(currentFinancialAccount)) {
    sections = Object.keys(navConfig).filter(s => navConfig[s].standardSection)
  }
  return sections
}

const accountNavigation = (sectionConfig, financialAccountType) => {
  const navigation = {}
  const sectionNames = Object.keys(sectionConfig)

  sectionNames.forEach((sectionName) => {
    let overrides = {}

    const {
      to,
      name,
      showBackWord,
      contactType,
      standardSection,
      isSuccessorDonor,
    } = sectionInformation(financialAccountType, sectionName)

    if (contactType) {
      overrides = {
        ...overrides,
        contactType,
      }
    }

    if (isSuccessorDonor) {
      overrides = {
        ...overrides,
        isSuccessorDonor,
      }
    }

    navigation[sectionName] = {
      to,
      name,
      showBackWord,
      standardSection,
      ...overrides,
    }
  })
  return navigation
}

const schwabOnePersonalSections = accountNavigation(
  sectionsList.schwabOnePersonalSection,
  SCHWAB_ONE_PERSONAL.SECTION
)

const schwabIRASections = accountNavigation(
  sectionsList.schwabIraSection,
  SCHWAB_IRA.SECTION
)

const schwabTrustSections = accountNavigation(
  sectionsList.schwabTrustSection,
  SCHWAB_ONE_TRUST.SECTION
)

const schwabCharitable = accountNavigation(
  sectionsList.schwabCharitableSection,
  SCHWAB_CHARITABLE.SECTION
)

const getAccountNavigation = (financialAccountType, jointOwner) => {
  let navigationConfig
  switch (financialAccountType) {
    case SCHWAB_ONE_PERSONAL.ACCOUNT_TYPE_ROUTE:
      navigationConfig = schwabOnePersonalSections
      break
    case SCHWAB_IRA.ACCOUNT_TYPE_ROUTE:
      navigationConfig = schwabIRASections
      break
    case SCHWAB_ONE_TRUST.ACCOUNT_TYPE_ROUTE:
      navigationConfig = schwabTrustSections
      break
    case SCHWAB_CHARITABLE.ACCOUNT_TYPE_ROUTE:
      navigationConfig = schwabCharitable
      break
    default:
      break
  }

  navigationConfig = { ...navigationConfig }

  if (!jointOwner) {
    delete navigationConfig.jointOwner
  }
  return navigationConfig
}

/**
 * Previous Section
 * Description: Get the previous section information for a section on a financial account.
 * Returns: Object
 * {
 *   name,
 *   url,
 *   showBackWord,
 * }
*/

const prevSectionInformation = (info) => {
  const {
    accountNavConfig,
    prevAccountNavConfig,
    prevFinancialAccount,
    prevFinancialAccountType,
    prevFinancialAccountId,
    financialAccounts,
    currentFinancialAccountType,
    currentFinancialAccountId,
    currentSection,
  } = info
  let overrides = {}
  const { beneficiaries: beneficiariesSection } = accountNavConfig

  if (beneficiariesSection) {
    overrides = {
      ...overrides,
      isSuccessorDonor: beneficiariesSection.isSuccessorDonor,
    }
  }

  let sectionNavigation = {}
  const sections = buildSectionList(
    accountNavConfig,
    financialAccounts,
    currentFinancialAccountId
  )

  const [firstSection] = sections
  if (currentSection === firstSection) {
    if (!prevFinancialAccount) {
      sectionNavigation = {
        to: { type: C.ONBOARDING_YOUR_ACCOUNTS },
        name: 'Your Accounts',
        showBackWord: false
      }
    } else {
      const [prevAccountLastSectionName] = Object.keys(prevAccountNavConfig).slice(-1)
      const prevAccountSectionConfig = prevAccountNavConfig[`${prevAccountLastSectionName}`]
      const prevAccountContactType = prevAccountSectionConfig.contactType
      sectionNavigation = {
        to: {
          type: prevAccountSectionConfig.to.type,
          payload: {
            ...overrides,
            financialAccountId: prevFinancialAccountId,
            financialAccountType: prevFinancialAccountType,
            contactType: prevAccountContactType,
          }
        },
        name: prevAccountSectionConfig.name,
        showBackWord: prevAccountSectionConfig.showBackWord
      }
    }
  } else {
    const sectionIndex = sections.indexOf(currentSection)
    const prevSectionName = sections[sectionIndex - 1]
    const prevSection = accountNavConfig[`${prevSectionName}`]

    if (prevSection.contactType) {
      sectionNavigation.nextSectionContactType = prevSection.contactType
    }
    sectionNavigation = {
      ...sectionNavigation,
      to: {
        type: prevSection.to.type,
        payload: {
          ...overrides,
          financialAccountId: currentFinancialAccountId,
          financialAccountType: currentFinancialAccountType,
          contactType: prevSection.contactType,
        }
      },
      name: prevSection.name,
      showBackWord: prevSection.showBackWord,
    }
  }

  return sectionNavigation
}

export const getLastSection = (financialAccounts) => {
  if (financialAccounts.length <= 0) {
    return
  }
  const [lastFinancialAccount] = financialAccounts.slice(-1)
  const {
    id,
    attributes: {
      financialAccountType,
    }
  } = lastFinancialAccount

  const routeType = rawFinancialAccountTypes[financialAccountType]
  const navigationConfig = getAccountNavigation(routeType)

  const sections = Object.keys(navigationConfig)
  const [lastSection] = sections.slice(-1)
  const { name, showBackWord, contactType, to } = navigationConfig[lastSection]
  const { type } = to
  const payload = { financialAccountId: id, financialAccountType: routeType, contactType }
  return {
    to: {
      type,
      payload
    },
    name,
    showBackWord
  }
}

export const getPreviousSection = (currentSectionInfo) => {
  const {
    currentFinancialAccountId,
    currentFinancialAccountType,
    currentSection,
    financialAccounts,
    jointOwner,
    isMobile,
  } = currentSectionInfo
  if (!isMobile) return

  let prevFinancialAccountId
  let prevFinancialAccountType
  let prevAccountNavConfig
  const accountNavConfig = getAccountNavigation(currentFinancialAccountType, jointOwner)
  const prevFinancialAccount = getPrevFinancialAccount(currentFinancialAccountId, financialAccounts)

  if (prevFinancialAccount) {
    const {
      attributes: { jointOwner: prevJointOwner }
    } = prevFinancialAccount
    prevFinancialAccountId = prevFinancialAccount.id
    prevFinancialAccountType = prevFinancialAccount.attributes.financialAccountType
    prevFinancialAccountType = getFinancialAccountData(prevFinancialAccountType, 'RAW_TYPE').ACCOUNT_TYPE_ROUTE
    prevAccountNavConfig = getAccountNavigation(prevFinancialAccountType, prevJointOwner)
  }

  return prevSectionInformation({
    accountNavConfig,
    prevAccountNavConfig,
    prevFinancialAccount,
    prevFinancialAccountType,
    prevFinancialAccountId,
    financialAccounts,
    currentFinancialAccountType,
    currentFinancialAccountId,
    currentSection,
  })
}

/**
 * Next Section
 * Description: Get the next section action for a section on a financial account.
 * Returns: Object,
 * {
 *  type, payload
 * }
*/

const getNextSectionAction = (info) => {
  const {
    accountNavConfig,
    nextFinancialAccount,
    nextFinancialAccountType,
    nextFinancialAccountId,
    financialAccounts,
    currentFinancialAccountType,
    currentFinancialAccountId,
    currentSection,
  } = info
  let nextSectionAction = {}
  const sections = buildSectionList(
    accountNavConfig,
    financialAccounts,
    currentFinancialAccountId
  )
  const [lastSection] = sections.slice(-1)
  let nextSectionActionType = C.ONBOARDING_ACCOUNT_DETAILS

  if (currentSection === lastSection) {
    if (!nextFinancialAccount) {
      nextSectionActionType = C.ONBOARDING_REVIEW_AND_SUBMIT
    }
    nextSectionAction = {
      type: nextSectionActionType,
      payload: {
        financialAccountType: nextFinancialAccountType,
        financialAccountId: nextFinancialAccountId
      }
    }
  } else {
    const sectionIndex = sections.indexOf(currentSection)
    const nextSectionName = sections[sectionIndex + 1]
    const nextSection = accountNavConfig[`${nextSectionName}`]
    let nextSectionContactType = null
    if (nextSection.contactType) {
      nextSectionContactType = nextSection.contactType
    }
    nextSectionAction = {
      type: nextSection.to.type,
      payload: {
        financialAccountType: currentFinancialAccountType,
        financialAccountId: currentFinancialAccountId,
        contactType: nextSectionContactType
      }
    }
  }
  return nextSectionAction
}

export const getNextSection = (currentSectionInfo) => {
  const {
    financialAccounts,
    currentFinancialAccountId,
    currentFinancialAccountType,
    currentSection,
    jointOwner,
    isUTMA,
    isCustodial,
  } = currentSectionInfo

  let nextFinancialAccountId
  let nextFinancialAccountType
  const accountNavConfig = getAccountNavigation(currentFinancialAccountType, jointOwner)
  const nextFinancialAccount = getNextFinancialAccount(currentFinancialAccountId, financialAccounts)

  if (nextFinancialAccount) {
    nextFinancialAccountId = nextFinancialAccount.id
    nextFinancialAccountType = nextFinancialAccount.attributes.financialAccountType
    nextFinancialAccountType =  getFinancialAccountData(nextFinancialAccountType, 'RAW_TYPE').ACCOUNT_TYPE_ROUTE
  }

  if (!isUTMA && !isCustodial) delete accountNavConfig.successorCustodians
  
  if (isUTMA) delete accountNavConfig.beneficiaries

  return getNextSectionAction({
    accountNavConfig,
    nextFinancialAccount,
    nextFinancialAccountType,
    nextFinancialAccountId,
    financialAccounts,
    currentFinancialAccountType,
    currentFinancialAccountId,
    currentSection,
  })
}



// WEBPACK FOOTER //
// ./src/utils/financialAccountTypeNavigation.js